import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Store } from '@ngrx/store';
import { noop } from 'rxjs';
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {
  BaseButtonSize,
  BaseCoreState,
  BaseCustomerInquiryOfferInterface,
  baseInquiryActions,
  BaseInquiryShifts,
  BaseInquiryShiftsType,
  BaseInquiryShiftsWithFlex,
  BaseModalService,
  BaseOfferStatus,
} from 'base';
import { BaseCustomerOfferPositionsToAcceptInterface } from '../../_interfaces/base-customer-offer-positions-to-accept.interface';
import { BaseCustomerOfferActions } from '../../_enums/base-customer-offer-actions.enum';
import { BaseCustomerLocationWorkCouncilSettingsInterface } from '../../../../shared/_interfaces/base-customer-location-work-council-settings.interface';
import { BaseCustomerOfferActionsService } from '../../../../shared/_services/base-customer-offer-actions.service';

@Component({
  selector: 'base-customer-offer-actions',
  templateUrl: './base-customer-offer-actions.component.html',
})
export class BaseCustomerOfferActionsComponent implements OnInit, OnChanges {
  @ViewChild('rejectOfferTemplate') rejectOfferTemplate: ElementRef;
  @ViewChild('acceptOfferModal') acceptOfferModal: ElementRef;
  @Input() offer: BaseCustomerInquiryOfferInterface;
  @Input() offerAction: BaseCustomerOfferActions;
  @Input() locationWorkCouncilSettings: BaseCustomerLocationWorkCouncilSettingsInterface;
  @Output() toAssignments: EventEmitter<BaseCustomerInquiryOfferInterface> =
    new EventEmitter<BaseCustomerInquiryOfferInterface>();

  modalRef: NgbModalRef;
  isOfferConfirmed: boolean;
  isOfferOpen: boolean;
  isOfferDeclined: boolean;
  isOfferConfirmationNeeded = false;
  isOfferUpdateNeeded = false;
  isAcceptOfferAvailable: boolean;
  isShiftsDeselectionMode: boolean;
  isAcceptButtonAvailable = false;
  isRejectButtonAvailable = false;
  isWithdrawButtonAvailable = false;
  isPreselectButtonAvailable = false;
  isUnselectButtonAvailable = false;
  profileName: string;
  offerPositionsToAccept: BaseCustomerOfferPositionsToAcceptInterface[];
  buttonSizes = BaseButtonSize;

  constructor(
    private store: Store<BaseCoreState>,
    private modalService: BaseModalService,
    private offerActionsService: BaseCustomerOfferActionsService
  ) {}

  ngOnInit() {
    this.setOfferCreationAvailability();
    this.setStatusValues();
    this.initializeActionButtonStates();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (this.offer.data.status === BaseOfferStatus.open && changes?.offer && !changes.offer.firstChange) {
      this.setOfferCreationAvailability();
    }
    if (changes?.offerAction && !changes?.offerAction.firstChange) {
      if (changes.offerAction.currentValue === BaseCustomerOfferActions.accept) {
        this.openAcceptOfferModal();
      }
      if (changes.offerAction.currentValue === BaseCustomerOfferActions.reject) {
        this.openRejectOfferModal();
      }
    }
  }

  openAssignmentsInfo(): void {
    this.toAssignments.emit(this.offer);
  }

  allShiftsSelection(): void {
    this.store.dispatch(
      baseInquiryActions.customerOfferAllShiftsSelection({
        payload: { offerId: this.offer.data.id, isShiftsDeselectionMode: this.isShiftsDeselectionMode },
      })
    );
  }

  openRejectOfferModal(): void {
    this.profileName = `${this.offer.data.profile.firstName} ${this.offer.data.profile.lastName}`;
    this.modalRef = this.modalService.open(
      {
        headText: 'inquiry.rejectOffer',
        bodyTemplate: this.rejectOfferTemplate,
        hideFooter: true,
      },
      { size: 'lg', backdrop: 'static' }
    );

    this.modalRef.result.then(() => noop, noop);
  }

  openAcceptOfferModal(): void {
    this.modalRef = this.modalService.open(
      this.isAcceptOfferAvailable
        ? {
            headText: 'inquiry.acceptOffer',
            bodyTemplate: this.acceptOfferModal,
            okText: 'action.yesAccept',
          }
        : {
            headText: 'inquiry.acceptOfferValidationPopup.headText',
            bodyText: 'inquiry.acceptOfferValidationPopup.bodyText',
            okButtonDisplayed: false,
            cancelButtonDisplayed: false,
          },
      { size: 'lg' }
    );

    this.modalRef.result.then(() => {
      this.acceptOffer();
    }, noop);
  }

  handleOfferPreselection(): void {
    this.store.dispatch(
      baseInquiryActions.customerInquiryOfferPreselection({
        payload: {
          inquiry: this.offer.data.inquiryId as number,
          offer: this.offer.data.id,
          preselect: !this.offer.data.preselected,
        },
      })
    );
  }

  private setOfferCreationAvailability(): void {
    this.offerPositionsToAccept = this.prepareOfferPositionsToAccept();
    this.isAcceptOfferAvailable = this.offerPositionsToAccept.every((position) => position.selectedShifts.length === 1);
    this.isShiftsDeselectionMode = this.offerPositionsToAccept.length > 0;
  }

  private prepareOfferPositionsToAccept(): BaseCustomerOfferPositionsToAcceptInterface[] {
    let positions: BaseCustomerOfferPositionsToAcceptInterface[] = [];

    this.offer.data.positions.forEach((positionItem) => {
      const dateItemByPositionDate = this.offer.dates.find((dateItem) => dateItem.date === positionItem.date);
      if (dateItemByPositionDate) {
        let selectedShifts: BaseInquiryShiftsType[] = [];
        if (positionItem.flexShift) {
          if (dateItemByPositionDate.shifts.some((shiftItem) => shiftItem.isSelected)) {
            selectedShifts.push(
              BaseInquiryShifts.fd || BaseInquiryShifts.zd || BaseInquiryShifts.sd || BaseInquiryShifts.nd
            );
          }
        } else {
          dateItemByPositionDate.shifts.forEach((shiftItem) => {
            if (shiftItem.shift !== BaseInquiryShiftsWithFlex.flex && shiftItem.isSelected) {
              selectedShifts.push(shiftItem.shift);
            }
          });
        }

        if (selectedShifts.length > 0) {
          positions.push({
            id: positionItem.id as number,
            selectedShifts,
          });
        }
      }
    });
    return positions;
  }

  private acceptOffer(): void {
    if (this.isAcceptOfferAvailable) {
      this.store.dispatch(
        baseInquiryActions.acceptOffer({
          payload: {
            inquiry: this.offer.data.inquiryId as number,
            offer: this.offer.data.id,
            offerPositions: this.offerPositionsToAccept.map((position) => {
              return { id: position.id, selectedShift: position.selectedShifts[0] };
            }),
          },
        })
      );
    }
  }

  private setStatusValues(): void {
    this.isOfferConfirmed = this.offer.data.status === BaseOfferStatus.confirmed;
    this.isOfferOpen = this.offer.data.status === BaseOfferStatus.open;
    this.isOfferDeclined = this.offer.data.status === BaseOfferStatus.declined;
    this.isOfferConfirmationNeeded = this.offer.data.status === BaseOfferStatus.needsConfirmation;
    this.isOfferUpdateNeeded = this.offer.data.status === BaseOfferStatus.needsUpdate;
  }

  private initializeActionButtonStates(): void {
    this.isAcceptButtonAvailable = this.offerActionsService.checkAcceptButtonAvailability(
      this.offer.data,
      this.locationWorkCouncilSettings
    );
    this.isRejectButtonAvailable = this.offerActionsService.checkRejectButtonAvailability(this.offer.data);
    this.isWithdrawButtonAvailable = this.offerActionsService.checkWithdrawButtonAvailability(this.offer.data);
    this.isPreselectButtonAvailable = this.offerActionsService.checkPreselectButtonAvailability(this.offer.data);
    this.isUnselectButtonAvailable = this.offerActionsService.checkUnselectButtonAvailability(
      this.offer.data,
      this.locationWorkCouncilSettings
    );
  }
}
